<template>
    <div>
        <api-errors :errors="issuesErrors" />
        <div class="panel panel-default">
            <div class="panel-heading">
                <div class="row">
                    <div class="col-md-9">
                        <div class="input-group">
                            <input v-model="search"
                                   type="text"
                                   class="form-control"
                                   placeholder="Search all issues"
                                   @keydown.enter="triggerSearchButton"
                            >
                            <div class="input-group-btn">
                                <a :href="generateUrl('search', search)"
                                   ref="searchButton"
                                   class="btn btn-default color-black"
                                >
                                    Search <i class="fa fa-search" aria-hidden="true" />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="btn-group">
                        <button v-if="itemId"  class="btn btn-success dropdown-toggle" data-toggle="dropdown"
                                aria-haspopup="true" aria-expanded="false">
                            New Issue  <span class="caret"></span>
                        </button>
                            <ul class="dropdown-menu">
                                <li><a href="#" @click="openIssuesModal">New Issue</a></li>

                                <li v-if="workgroupPermValidation"><a href="#" @click="createProposedChanges">Propose Change</a></li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="row" :class="{'margin-above': areThereAnyAppliedFilters}">
                    <div class="col-md-12">
                        <a v-if="areThereAnyAppliedFilters"
                           :href="getWindowLocation()"
                           class="btn btn-default btn-sm color-black"
                           role="button"
                           title="Clear all filters"
                        >
                            <i class="fa fa-filter" aria-hidden="true" />
                            <em>Clear all filters</em>
                            <i class="fas fa-times" aria-hidden="true" />
                        </a>
                        <a v-for="(value, key) in appliedFiltersData"
                           :href="removeParamFromUrl(key, value.value)"
                           class="btn btn-default btn-sm margin-right-small color-black"
                           role="button"
                           :title="`Remove ${key} filter`"
                           :key="key"
                        >
                            <i class="fa fa-filter" aria-hidden="true" />
                            <em>{{ key }}</em>: {{ value.title }}
                            <i class="fa fa-times" aria-hidden="true" />
                        </a>
                    </div>
                </div>
            </div>
            <div class="panel-body">
                <div class="margin-below">
                    <div class="btn-group">
                        <a :href="generateUrl('isopen', true)"
                           class="btn"
                           :class="{'bold-font btn-primary pointer-events-none': showOpenIssues}"
                           role="button"
                        >
                            Open
                        </a>
                        <a :href="generateUrl('isopen', false)"
                           class="btn"
                           :class="{'bold-font btn-danger pointer-events-none': !showOpenIssues}"
                           role="button"
                        >
                            Closed
                        </a>
                    </div>
                    <div class="margin-above">
                        {{ !isLoading ? getShowingText() : "&ZeroWidthSpace;" }}
                        <hr class="no-margin">
                    </div>
                </div>
                <table class="table table-layout-fixed">
                    <thead>
                    <tr>
                        <th scope="col">
                            Issue name
                        </th>
                        <th scope="col" class="col">
                            Description
                        </th>
                        <th v-if="!itemId" scope="col">
                            Item
                        </th>
                        <th scope="col" class="very-small-col">
                            <sorting-arrows text="Created" :ordering="ordering" asc-param="-created" desc-param="created" />
                        </th>
                        <th scope="col" class="very-small-col">
                            <sorting-arrows text="Updated" :ordering="ordering" asc-param="-bumped_date" desc-param="bumped_date" />
                        </th>
                        <th scope="col" class="very-centered-small-col">
                            <i class="fa fa-comment-o" aria-hidden="true"/>
                        </th>
                    </tr>
                    </thead>
                    <tbody v-if="itemIssues.length > 0">
                    <tr v-for="issue in itemIssues" :key="issue.id">
                        <td>
                            <div>
                                <a :href="issue.url" class="no-link-decoration" style="white-space: normal; overflow-wrap: break-word;">
                                    <open-close-issues :is-open="issue.isopen" :is-alert="false" :include-text="false" />
                                    {{ issue.name }}&nbsp;<span class="text-muted"><em>#{{ issue.pk }}</em></span>
                                </a>
                                <div class="margin-left">
                                    <small>
                                        <i class="fa fa-fw fa-user text-muted" aria-hidden="true" title="Submitter" />
                                        <a :href="generateUrl('submitter', issue.submitter_id)" title="Filter by submitter">
                                            {{ issue.submitter_display_name }}
                                        </a>
                                    </small>
                                </div>
                                <div v-if="!workgroupId && issue.workgroup_id" class="margin-left">
                                    <small>
                                        <i class="fa fa-fw fa-users text-muted" aria-hidden="true" title="Workgroup" />
                                        <a
                                            v-if="issue.workgroup_id"
                                            :href="generateUrl('workgroup', issue.workgroup_id)"
                                            title="Filter by Workgroup"
                                        >
                                            {{ issue.workgroup_name }}
                                        </a>
                                        <span v-else>
                                            {{ issue.workgroup_name }}
                                        </span>
                                    </small>
                                </div>
                                <div v-if="issue.stewardship_organisation_name" class="margin-left">
                                    <small>
                                        <i class="fa fa-fw fa-sitemap text-muted" aria-hidden="true" title="Stewardship Organisation" />
                                        <a
                                            :href="generateUrl('stewardship_organisation', issue.stewardship_organisation_uuid)"
                                            title="Filter by Stewardship Organisation"
                                        >
                                            {{ issue.stewardship_organisation_name }}
                                        </a>
                                    </small>
                                </div>
                                <div v-if="issue.label_names.length" class="label-container">
                                        <div v-for="label in issue.label_names" :key="label.pk" class="label-container">
                                            <issue-label :label="label.label" :filterable="true" size="xs" />
                                        </div>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div v-if="issue.description" style="white-space: normal; overflow-wrap: break-word;">
                                {{descriptionCharacterLimit>issue.description.length ? issue.description : issue.description.substring(0,descriptionCharacterLimit)+" ..."}}
                            </div>
                            <div v-else>
                                -
                            </div>
                        </td>
                        <td v-if="!itemId">
                            <div v-html="issue.item_statuses_html">
                            </div>
                        </td>
                        <td>
                            <small :title="'Created on '+issue.created.substring(0, 10)">
                                {{ humanizedTime(issue.created) }}
                            </small>
                        </td>
                        <td>
                            <small v-if="issue.bumped_date" :title="'Last updated on '+issue.bumped_date.substring(0, 10)">
                                {{ humanizedTime(issue.bumped_date) }}
                            </small>
                            <small v-else>
                                -
                            </small>
                        </td>
                        <td>
                            <a v-if="issue.number_of_comments > 0" :title="issue.number_of_comments+' Comments'" class="no-link-decoration"
                               :href="`${issue.url}#end_comment`"
                            >
                                <i class="fa fa-comment-o" aria-hidden="true" /> {{ issue.number_of_comments }}
                            </a>
                            <div v-else>
                                -
                            </div>
                        </td>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr v-if="!isLoading">
                        <td>
                            {{ getNoIssuesMessage() }}
                        </td>
                    </tr>
                    </tbody>
                </table>
                <loading v-if="isLoading" />
                <div class="text-center">
                    <pagination
                        v-if="totalIssues > 25"
                        v-model="currentPage"
                        :total-page="totalPage"
                        size="sm"
                        @change="(pageIndex) => changePage(pageIndex)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiErrors from '@/ApiErrorDisplay.vue'
import apiRequest from 'src/mixins/api-request.js'
import moment from "moment"
import Pagination from 'uiv/src/components/pagination/Pagination.vue'
import Loading from "@/Loading.vue"
import urlManipulator from "src/mixins/url-manipulator.js"
import SortingArrows from "@/SortingArrows.vue"
import OpenCloseIssues from "@/issues/OpenCloseIssues.vue"
import IssueLabel from "@/issues/labels/IssueLabel.vue"

export default {
    name: "Issues",
    filters: {
        pluralize(word, amount) {
            if (amount > 1 || amount === 0) {
                return `${word}s`
            }
            return word
        }
    },
    components: {
        apiErrors,
        Pagination,
        Loading,
        SortingArrows,
        OpenCloseIssues,
        IssueLabel,
    },
    mixins: [apiRequest, urlManipulator],
    props: {
        apiUrl: {
            type: String,
            required: true,
        },
        itemId: {
            type: String,
            default: "",
        },
        workgroupId: {
            type: String,
            default: "",
        },
        appliedFilters: {
            type: String,
            default: "",
        },
        url: {
            type: String,
            required: true
        },
        workgroupPermValidation: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        itemIssues: [],
        issueModalOpen: false,
        showOpenIssues: true,
        time: "",
        issuesErrors: {},
        appliedFiltersData: {},
        search: '',
        currentPage: 1,
        totalIssues: 1,
        totalPage: 5,
        isLoading: true,
        ordering: '',
        descriptionCharacterLimit: 280,
        // 280 represents a sensible description length
    }),
    computed: {
        humanizedTime: function () {
            // This computed function is here since we need to pass parameters in order to calculate the time, and
            // is executed every second (when this.time is updated).
            return function (isoTime) {
                return moment(isoTime).from(this.time)
            }
        },
        areThereAnyAppliedFilters: function () {
            return Object.keys(this.appliedFiltersData).length > 0
        },
    },
    mounted() {
        if (this.appliedFilters) {
            this.appliedFiltersData = JSON.parse(this.appliedFilters)
        }

        let orderParam = this.getURLSearchParams().get('order')

        if (orderParam && ['created', '-created', 'bumped_date', '-bumped_date'].includes(orderParam)) {
            this.ordering = orderParam
        } else {
            this.ordering = '-bumped_date'
        }

        this.time = new Date().toISOString()
        setInterval(() => {
            this.time = new Date().toISOString()
        }, 60000)

        let params = {}
        if (this.itemId) {
            params['item'] = this.itemId
        }
        if (this.workgroupId) {
            params['workgroup'] = this.workgroupId
        }
        for (const [key, filter] of Object.entries(this.appliedFiltersData)) {
            params[key] = filter.value
            if (key === 'isopen') {
                this.showOpenIssues = (filter.value === 'true')
            }
            if (key === 'page') {
                this.currentPage = parseInt(filter.value)
            }
        }

        if (this.appliedFiltersData['search']) {
            this.search = this.appliedFiltersData['search']['value']
        }

        // Remove unwanted JavaScript object representations of applied_filters from array to avoid them displaying:
        delete this.appliedFiltersData['page']
        delete this.appliedFiltersData['isopen']
        delete this.appliedFiltersData['order']
        delete this.appliedFiltersData['search']
        this.get(
            this.apiUrl,
            params,
        ).then((response) => {
            this.totalIssues = response.data.count
            this.totalPage = Math.ceil(this.totalIssues / 25)
            this.isLoading = false
            for (const issue of response.data.results) {
                this.itemIssues.push(issue)
            }
        }).catch((error) => {
            this.issuesErrors = JSON.parse(error.response.data)
        })
    },
    methods: {
        openIssuesModal: function () {
            this.$emit('openissuesmodal')
        },
        createProposedChanges: function () {
            let itemUrl = this.url
            window.location.assign(itemUrl)
        },
        getWindowLocation: function () {
            return window.location.pathname
        },
        getNoIssuesMessage: function () {
            if (this.areThereAnyAppliedFilters) {
                return `There are no ${this.showOpenIssues ? "open" : "closed"} issues that match your filters.`
            } else {
                return `There are no ${this.showOpenIssues ? "open" : "closed"} issues.`
            }
        },
        getShowingText: function () {
            if (this.totalPage < 2) {
                return `Showing ${this.itemIssues.length} ${this.$options.filters.pluralize(' issue', this.itemIssues.length)}.`
            } else {
                return `Showing ${this.itemIssues.length} ${this.$options.filters.pluralize(' issue', this.itemIssues.length)} of ${this.totalIssues}.`
            }

        },
        triggerSearchButton: function () {
            this.$refs.searchButton.click()
        },
        changePage: function (pageIndex) {
            location.replace(this.generateUrl('page', pageIndex, true))
        },
    },
}
</script>

<style scoped>
.pointer-events-none:focus {
    outline: none;
}
</style>
